import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { z } from "zod";
import { ClipboardList, MessageSquare } from "lucide-react";
import ExerciseLogInput, { Exercise } from "./ExerciseLogInput";

interface SessionNotesData {
  id: string;
  workout_summary: string | null;
  exercises_performed: Exercise[] | null;
  client_energy_level: number | null;
  client_mood: string | null;
  trainer_notes: string | null;
  client_visible_notes: string | null;
  next_session_focus: string | null;
  homework: string | null;
}

interface AddSessionNotesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  sessionId: string;
  sessionDate: string;
  clientName: string;
  existingNotes?: SessionNotesData | null;
}

const sessionNotesSchema = z.object({
  workout_summary: z.string().max(1000).optional().nullable(),
  client_energy_level: z.number().min(1).max(5).optional().nullable(),
  client_mood: z.string().max(50).optional().nullable(),
  trainer_notes: z.string().max(2000).optional().nullable(),
  client_visible_notes: z.string().max(2000).optional().nullable(),
  next_session_focus: z.string().max(500).optional().nullable(),
  homework: z.string().max(1000).optional().nullable(),
});

const moodOptions = [
  "Energetic",
  "Motivated",
  "Focused",
  "Tired",
  "Stressed",
  "Recovering",
  "Other",
];

const AddSessionNotesDialog = ({
  open,
  onOpenChange,
  onSuccess,
  sessionId,
  sessionDate,
  clientName,
  existingNotes,
}: AddSessionNotesDialogProps) => {
  const [workoutSummary, setWorkoutSummary] = useState("");
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [energyLevel, setEnergyLevel] = useState<string>("");
  const [mood, setMood] = useState("");
  const [trainerNotes, setTrainerNotes] = useState("");
  const [clientVisibleNotes, setClientVisibleNotes] = useState("");
  const [nextSessionFocus, setNextSessionFocus] = useState("");
  const [homework, setHomework] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open && existingNotes) {
      setWorkoutSummary(existingNotes.workout_summary || "");
      setExercises(existingNotes.exercises_performed || []);
      setEnergyLevel(existingNotes.client_energy_level?.toString() || "");
      setMood(existingNotes.client_mood || "");
      setTrainerNotes(existingNotes.trainer_notes || "");
      setClientVisibleNotes(existingNotes.client_visible_notes || "");
      setNextSessionFocus(existingNotes.next_session_focus || "");
      setHomework(existingNotes.homework || "");
    } else if (open) {
      resetForm();
    }
  }, [open, existingNotes]);

  const resetForm = () => {
    setWorkoutSummary("");
    setExercises([]);
    setEnergyLevel("");
    setMood("");
    setTrainerNotes("");
    setClientVisibleNotes("");
    setNextSessionFocus("");
    setHomework("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const data = {
        workout_summary: workoutSummary || null,
        exercises_performed: exercises.length > 0 ? (exercises as unknown as Json) : null,
        client_energy_level: energyLevel ? parseInt(energyLevel) : null,
        client_mood: mood || null,
        trainer_notes: trainerNotes || null,
        client_visible_notes: clientVisibleNotes || null,
        next_session_focus: nextSessionFocus || null,
        homework: homework || null,
      };

      sessionNotesSchema.parse(data);

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      setIsSubmitting(true);

      if (existingNotes) {
        const { error } = await supabase
          .from("session_notes")
          .update(data)
          .eq("id", existingNotes.id);

        if (error) throw error;
        toast.success("Session notes updated");
      } else {
        const { error } = await supabase.from("session_notes").insert({
          ...data,
          session_id: sessionId,
          user_id: user.id,
        });

        if (error) throw error;
        toast.success("Session notes saved");
      }

      resetForm();
      onOpenChange(false);
      onSuccess();
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        toast.error(error.errors[0].message);
      } else {
        console.error("Error saving session notes:", error);
        toast.error("Failed to save session notes");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {existingNotes ? "Edit session notes" : "Add session notes"}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            {clientName} - {sessionDate}
          </p>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Tabs defaultValue="workout" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="workout" className="gap-2">
                <ClipboardList className="h-4 w-4" />
                Workout
              </TabsTrigger>
              <TabsTrigger value="notes" className="gap-2">
                <MessageSquare className="h-4 w-4" />
                Notes
              </TabsTrigger>
            </TabsList>

            <TabsContent value="workout" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="summary">Workout summary</Label>
                <Textarea
                  id="summary"
                  placeholder="Brief overview of today's session..."
                  value={workoutSummary}
                  onChange={(e) => setWorkoutSummary(e.target.value)}
                  rows={2}
                />
              </div>

              <ExerciseLogInput exercises={exercises} onChange={setExercises} />

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="energy">Client energy level</Label>
                  <Select value={energyLevel} onValueChange={setEnergyLevel}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 - Very low</SelectItem>
                      <SelectItem value="2">2 - Low</SelectItem>
                      <SelectItem value="3">3 - Moderate</SelectItem>
                      <SelectItem value="4">4 - High</SelectItem>
                      <SelectItem value="5">5 - Very high</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mood">Client mood</Label>
                  <Select value={mood} onValueChange={setMood}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select mood" />
                    </SelectTrigger>
                    <SelectContent>
                      {moodOptions.map((option) => (
                        <SelectItem key={option} value={option.toLowerCase()}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="notes" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="clientNotes">
                  Notes for client
                  <span className="text-xs text-muted-foreground ml-2">
                    (visible in client portal)
                  </span>
                </Label>
                <Textarea
                  id="clientNotes"
                  placeholder="Great workout today! Focus on..."
                  value={clientVisibleNotes}
                  onChange={(e) => setClientVisibleNotes(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="homework">Homework / at-home exercises</Label>
                <Textarea
                  id="homework"
                  placeholder="Stretches, exercises to practice at home..."
                  value={homework}
                  onChange={(e) => setHomework(e.target.value)}
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="nextFocus">Next session focus</Label>
                <Textarea
                  id="nextFocus"
                  placeholder="What to focus on in the next session..."
                  value={nextSessionFocus}
                  onChange={(e) => setNextSessionFocus(e.target.value)}
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="trainerNotes">
                  Private trainer notes
                  <span className="text-xs text-muted-foreground ml-2">
                    (not visible to client)
                  </span>
                </Label>
                <Textarea
                  id="trainerNotes"
                  placeholder="Internal notes, observations..."
                  value={trainerNotes}
                  onChange={(e) => setTrainerNotes(e.target.value)}
                  rows={2}
                />
              </div>
            </TabsContent>
          </Tabs>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? "Saving..."
                : existingNotes
                ? "Update Notes"
                : "Save Notes"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddSessionNotesDialog;
